/**
 * Webhook Types for Event Notifications
 *
 * Webhooks allow external systems to receive real-time notifications
 * when events occur in SimCheck.ai.
 */
// ============================================================================
// Webhook Event Definitions
// ============================================================================
/**
 * All available webhook events with definitions
 */
export const WEBHOOK_EVENT_DEFINITIONS = [
    // Execution events
    { event: 'execution.started', category: 'execution', displayName: 'Execution Started', description: 'Triggered when a flow execution begins' },
    { event: 'execution.completed', category: 'execution', displayName: 'Execution Completed', description: 'Triggered when a flow execution completes successfully' },
    { event: 'execution.failed', category: 'execution', displayName: 'Execution Failed', description: 'Triggered when a flow execution fails' },
    { event: 'execution.cancelled', category: 'execution', displayName: 'Execution Cancelled', description: 'Triggered when a flow execution is cancelled' },
    // Test run events
    { event: 'test_run.created', category: 'test_run', displayName: 'Test Run Created', description: 'Triggered when a test run is created or scheduled' },
    { event: 'test_run.started', category: 'test_run', displayName: 'Test Run Started', description: 'Triggered when a test run starts executing' },
    { event: 'test_run.completed', category: 'test_run', displayName: 'Test Run Completed', description: 'Triggered when a test run completes' },
    { event: 'test_run.failed', category: 'test_run', displayName: 'Test Run Failed', description: 'Triggered when a test run fails' },
    { event: 'test_run.cancelled', category: 'test_run', displayName: 'Test Run Cancelled', description: 'Triggered when a test run is cancelled' },
    // Schedule events
    { event: 'schedule.triggered', category: 'schedule', displayName: 'Schedule Triggered', description: 'Triggered when a scheduled test is executed' },
    { event: 'schedule.missed', category: 'schedule', displayName: 'Schedule Missed', description: 'Triggered when a scheduled test misses its execution window' },
    // Device events
    { event: 'device.online', category: 'device', displayName: 'Device Online', description: 'Triggered when an edge device comes online' },
    { event: 'device.offline', category: 'device', displayName: 'Device Offline', description: 'Triggered when an edge device goes offline' },
    { event: 'device.error', category: 'device', displayName: 'Device Error', description: 'Triggered when an edge device reports an error' },
    // Modem events
    { event: 'modem.connected', category: 'modem', displayName: 'Modem Connected', description: 'Triggered when a modem connects to an edge device' },
    { event: 'modem.disconnected', category: 'modem', displayName: 'Modem Disconnected', description: 'Triggered when a modem disconnects from an edge device' },
    // eSIM events
    { event: 'esim.downloaded', category: 'esim', displayName: 'eSIM Downloaded', description: 'Triggered when an eSIM profile is downloaded' },
    { event: 'esim.expired', category: 'esim', displayName: 'eSIM Expired', description: 'Triggered when an eSIM profile expires' },
    // Batch events
    { event: 'batch.started', category: 'batch', displayName: 'Batch Started', description: 'Triggered when a batch execution starts' },
    { event: 'batch.completed', category: 'batch', displayName: 'Batch Completed', description: 'Triggered when a batch execution completes' },
    { event: 'batch.failed', category: 'batch', displayName: 'Batch Failed', description: 'Triggered when a batch execution fails' },
    // Artifact events
    { event: 'execution.artifact_ready', category: 'artifact', displayName: 'Artifact Ready', description: 'Triggered when a PCAP, AT log, voice sample, or other execution artifact has been stored and is downloadable' },
    { event: 'execution.ssi_computed', category: 'artifact', displayName: 'SSI Computed', description: 'Triggered when the composite SSI KPI is computed for an execution or rollup' },
    // Report events
    { event: 'report.digest_sent', category: 'report', displayName: 'Digest Sent', description: 'Triggered when a scheduled report digest is emailed to recipients' },
    { event: 'report.export_ready', category: 'report', displayName: 'Export Ready', description: 'Triggered when an on-demand report export (PDF/XLSX/CSV) completes' },
];
/**
 * Get events by category
 */
export function getEventsByCategory(category) {
    return WEBHOOK_EVENT_DEFINITIONS.filter(e => e.category === category);
}
/**
 * Get all event categories
 */
export function getEventCategories() {
    return ['execution', 'test_run', 'schedule', 'device', 'modem', 'esim', 'batch'];
}
// ============================================================================
// Webhook Signature
// ============================================================================
/**
 * Generate webhook signature header value
 * Format: "sha256=<hex-encoded-hmac>"
 */
export function generateWebhookSignature(payload, secret) {
    // Note: Actual implementation uses crypto.createHmac in Node.js
    // This is just the type definition
    return `sha256=<hmac>`;
}
/**
 * Verify webhook signature
 */
export function verifyWebhookSignature(payload, signature, secret) {
    // Note: Actual implementation compares HMAC
    return true;
}
